import React, { useCallback, useEffect, useRef } from 'react';
import { StyleSheet, ViewStyle } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { FilterChip } from '@/components/FilterChip';
import { SizedBox } from '@/components/SizedBox';

export type FilterChipItem<T extends string | number = string> = {
    id: T;
    label: string;
    testID?: string;
};

type FilterChipListProps<T extends string | number> = {
    items: FilterChipItem<T>[];
    selected: T;
    onSelect: (item: T) => void;
    style?: ViewStyle;
    contentContainerStyle?: ViewStyle;
};

const ItemSeparatorComponent = () => <SizedBox value={8} />;
const keyExtractor = <T extends string | number>(item: FilterChipItem<T>) => item.id.toString();

export const FilterChipList = <T extends string | number>({
    items,
    selected,
    onSelect,
    style,
    contentContainerStyle,
}: FilterChipListProps<T>) => {
    const listRef = useRef<FlatList<FilterChipItem<T>>>(null);
    const selectedIndex = items.findIndex(i => i.id === selected);

    useEffect(() => {
        if (selectedIndex !== -1) {
            listRef.current?.scrollToIndex({ index: selectedIndex, animated: true, viewPosition: 0.5 });
        }
    }, [selectedIndex]);

    const renderItem = useCallback(
        ({ item }: { item: FilterChipItem<T> }) => (
            <FilterChip
                label={item.label}
                active={item.id === selected}
                onPress={() => onSelect(item.id)}
                testID={item.testID}
            />
        ),
        [selected, onSelect]
    );

    const onScrollToIndexFailed = useCallback(
        (error: { index: number; highestMeasuredFrameIndex: number; averageItemLength: number }) => {
            // a freshly mounted list has no measured chips yet, so retry once they have laid out
            if (error.averageItemLength === 0) {
                requestAnimationFrame(() =>
                    listRef.current?.scrollToIndex({ index: error.index, animated: false, viewPosition: 0.5 })
                );
                return;
            }
            listRef.current?.scrollToOffset({
                offset: error.averageItemLength * error.index,
                animated: true,
            });
        },
        []
    );

    return (
        <FlatList
            ref={listRef}
            horizontal
            data={items}
            initialNumToRender={items.length}
            keyExtractor={keyExtractor}
            showsHorizontalScrollIndicator={false}
            ItemSeparatorComponent={ItemSeparatorComponent}
            style={style}
            contentContainerStyle={[styles.default, contentContainerStyle]}
            renderItem={renderItem}
            onScrollToIndexFailed={onScrollToIndexFailed}
        />
    );
};

const styles = StyleSheet.create({
    default: {
        minWidth: '100%',
    },
});
